import * as React from 'react';

import $ from 'jquery';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

import { Typography2 } from '@coursera/cds-core';

import AvailabilityPicker from 'bundles/author-sessions/components/sessionItem/AvailabilityPicker';
import DeadlinePicker from 'bundles/author-sessions/components/sessionItem/DeadlinePicker';
import type SessionItem from 'bundles/author-sessions/models/SessionItem';
import HelpTooltip from 'bundles/authoring/common/components/HelpTooltip';
import {
  updateLearnerItemDeadline,
  updateLearnerItemSchedule,
} from 'bundles/student-support-dashboard/actions/LearnerScheduleActions';

import _t from 'i18n!nls/author-assignment-grading';

import 'css!./__styles__/ItemTimedReleaseDeadlineEditor';

type Props = {
  item?: SessionItem;
  disableInputs?: boolean;
  userId: string;
  parentEl?: string;
};

type State = {
  clientY: number;
};

class ItemTimedReleaseDeadlineEditor extends React.Component<Props, State> {
  static contextTypes = {
    executeAction: PropTypes.func,
  };

  static defaultProps = {
    disableInputs: false,
    parentEl: 'body',
  };

  state: State = {
    clientY: 0,
  };

  handleShowDatePicker = () => {
    const { parentEl = 'body' } = this.props;
    $(parentEl).addClass('show-date-picker');
  };

  handleHideDatePicker = () => {
    const { parentEl = 'body' } = this.props;
    $(parentEl).removeClass('show-date-picker');
  };

  getUpdateDeadlineHandler = () => {
    // We know/assume that the item *in `props`* will be defined by the time this callback executes;
    // however, just in case it really is falsy, init to {} so we don't just crash on accessing a property of undefined.
    // Alternatively, we could terminate execution of `item` is not defined, but that changes runtime behavior and I don't know the scope of testing that.
    const item = (this.props.item || {}) as SessionItem;

    return (deadlineMoment?: moment.Moment) => {
      if (deadlineMoment) {
        this.context.executeAction(updateLearnerItemDeadline, {
          sessionId: item.sessionId,
          itemId: item.itemId,
          deadlineMoment,
        });
      } else {
        // BE does not support deleting overrides right now, so we will
        // 'override' it to the original deadline
        this.context.executeAction(updateLearnerItemDeadline, {
          sessionId: item.sessionId,
          itemId: item.itemId,
          deadlineMoment: item.originalItemDeadline,
        });
      }
    };
  };

  getUpdateScheduleOverrideHandler = () => {
    // We know/assume that the item *in `props`* will be defined by the time this callback executes;
    // however, just in case it really is falsy, init to {} so we don't just crash on accessing a property of undefined.
    // Alternatively, we could terminate execution of `item` is not defined, but that changes runtime behavior and I don't know the scope of testing that.
    const item = (this.props.item || {}) as SessionItem;

    return (arg: { startDate?: moment.Moment; endDate?: moment.Moment }) => {
      const { startDate, endDate } = arg;

      if (startDate && endDate) {
        // convert moment dates back into durations for API compatibility
        const unlockTime = startDate ? moment(startDate).diff(item.sessionStartDate) : undefined;
        const relockTime = endDate ? moment(endDate).diff(item.sessionStartDate) : undefined;

        this.context.executeAction(updateLearnerItemSchedule, {
          sessionId: item.sessionId,
          itemId: item.id,
          unlockTime,
          relockTime,
        });
      } else {
        // BE does not support deleting overrides right now, so we will
        // 'override' it to the original availability
        const availabilityStartDate = item.originalAvailability ? item.originalAvailability.startDate : null;
        const availabilityEndDate = item.originalAvailability ? item.originalAvailability.endDate : null;
        const unlockTime = availabilityStartDate
          ? moment(availabilityStartDate).diff(item.sessionStartDate)
          : undefined;
        const relockTime = availabilityEndDate ? moment(availabilityEndDate).diff(item.sessionStartDate) : undefined;

        this.context.executeAction(updateLearnerItemSchedule, {
          sessionId: item.sessionId,
          itemId: item.id,
          unlockTime,
          relockTime,
        });
      }
    };
  };

  getClientYOnClick = (event: $TSFixMe) => {
    const { clientY } = event;
    this.setState({
      clientY,
    });
  };

  render() {
    const { item, disableInputs = false, userId = '', parentEl } = this.props;
    const { clientY } = this.state;

    if (!item) {
      return null;
    }

    const deadline = item.deadline;
    const weekDeadline = item.weekDeadline;
    // An item-level deadline cannot be before the module's start date, which is seven days
    // before its module's ending date
    const minDate = moment(item.sessionStartDate).add(1, 'day');
    const availabilityStartDate = item.availabilityStartDate;
    const availabilityEndDate = item.availabilityEndDate;

    // the dates are clearable only if they are overridden/different from original dates.
    const isDeadlineOverridden = item.overriddenItemDeadline
      ? !item.overriddenItemDeadline.isSame(item.originalItemDeadline, 'minute')
      : false;

    const isAvailabilityOverridden = item.overriddenAvailability
      ? !item.originalAvailability || // no original availability set
        !item.overriddenAvailability.startDate.isSame(item.originalAvailability.startDate) || // different start
        !item.overriddenAvailability.endDate.isSame(item.originalAvailability.endDate) // or end date
      : false;

    return (
      <div className="rc-ItemTimedReleaseDeadlineEditor">
        <Typography2 variant="subtitleMedium" component="label">
          {_t('Item Availability')}
          <HelpTooltip
            message={_t(
              'This item will become available to learner(s) at the start date and time. This item will become unavailable to learner(s) on the end date and time.'
            )}
          />
        </Typography2>
        <div className="item-availability-editor">
          <AvailabilityPicker
            key={`item-availability-${item.id}-${userId}`}
            startDate={availabilityStartDate}
            endDate={availabilityEndDate}
            minDate={item.sessionStartDate}
            maxDate={deadline && item.hasGradingLatePenalty ? item.getHardDeadlineDate(deadline) : item.sessionEndDate}
            itemDeadline={deadline || weekDeadline}
            onChange={this.getUpdateScheduleOverrideHandler()}
            itemId={item.id}
            onShow={this.handleShowDatePicker}
            onHide={this.handleHideDatePicker}
            sessionId={item.sessionId}
            readOnly={disableInputs}
            item={item}
            openDirection="right"
            isClearable={isAvailabilityOverridden}
            clientY={clientY}
            getClientYOnClick={this.getClientYOnClick}
            parentEl={parentEl}
          />
        </div>
        <Typography2 variant="subtitleMedium" component="label">
          {_t('Item Deadline')}
          <HelpTooltip
            message={_t('The due date for this assignment. By default the deadline is based on the session schedule.')}
          />
        </Typography2>
        <div className="item-deadline-editor">
          <DeadlinePicker
            key={`item-deadline-${item.id}-${userId}`}
            date={deadline}
            defaultDate={weekDeadline}
            minDate={minDate}
            maxDate={item.sessionEndDate}
            onShow={this.handleShowDatePicker}
            onHide={this.handleHideDatePicker}
            onChange={this.getUpdateDeadlineHandler()}
            itemId={item.id}
            sessionId={item.sessionId}
            itemAvailability={item.availability}
            item={item}
            readOnly={disableInputs}
            openDirection="right"
            isClearable={isDeadlineOverridden}
            clientY={clientY}
            getClientYOnClick={this.getClientYOnClick}
            parentEl={parentEl}
          />
        </div>
      </div>
    );
  }
}

export default ItemTimedReleaseDeadlineEditor;
